/**
 * F11 — Admin venue-claim review / decision page.
 *
 * Route: /venue-claims/:id
 * Permission: venue_claims:review
 *
 * Mirrors the F09 VerificationDetailPage UX 1:1 with name substitutions and
 * F11-specific affordances:
 *
 *   Left column:
 *     - Venue card (composes the F02 `VenueCard` primitive)
 *
 *   Right column:
 *     - Claimant identity (avatar + display name + email)
 *     - B2B account panel (display name + claim metadata)
 *     - Tabs:
 *         * Documents — DocumentTile cards with a per-row "Download" button that
 *           opens a small reason dialog before fetching the 5-min presigned GET
 *         * History — VerificationTimeline rendering the lifecycle
 *         * Site visit notes — read-only textarea + "Edit notes" dialog
 *
 *   Bottom sticky bar:
 *     - Approve  (Dialog: optional reason + super_admin-only "Approve without
 *                 documents" toggle; warning banner when no commercial_register
 *                 doc is attached)
 *     - Reject   (Dialog: required reason ≥10 chars; RejectionReasonPicker)
 *     - Revoke   (Dialog: required reason ≥10 chars; only when status='approved')
 *
 * All dialog forms are RHF + Zod validated. The current admin role is read from
 * AuthContext.adminUser.role to gate the override toggle.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useI18n } from '@bravemove/i18n/react';
import {
  Avatar,
  Badge,
  Banner,
  Button,
  Card,
  Checkbox,
  Chip,
  Dialog,
  DocumentTile,
  EmptyState,
  FormField,
  Modal,
  RejectionReasonPicker,
  Spinner,
  StatusChip,
  type StatusChipStatus,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Textarea,
  VerificationTimeline,
  type VerificationTimelineItem,
  useToast,
} from '@bravemove/ui-web';
import { UIIcon } from '@bravemove/design-tokens/icons';
import {
  ApproveVenueClaimInputSchema,
  RejectVenueClaimInputSchema,
  VenueClaimSiteVisitNotesInputSchema,
  type VenueClaimDocument,
  type VenueClaimEntityStatus,
} from '@bravemove/types';
import { adminVenueClaimApi, type VenueClaimDetailResponse } from '../api/venue-claim.js';
import { translateApiError } from '../auth/errors.js';
import { useAuth } from '../auth/useAuth.js';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_CHIP_MAP: Record<VenueClaimEntityStatus, StatusChipStatus> = {
  draft: 'pending',
  pending: 'in_review',
  approved: 'approved',
  rejected: 'rejected',
  withdrawn: 'pending',
  revoked_by_admin: 'rejected',
};

function formatDate(iso: string | null, locale: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    );
  } catch {
    return iso;
  }
}

function statusKey(s: VenueClaimEntityStatus): string {
  return s === 'revoked_by_admin' ? 'revoked' : s;
}

// ─── Document download reason dialog ─────────────────────────────────────────

function DocumentDownloadDialog({
  open,
  loading,
  error,
  onClose,
  onConfirm,
}: {
  open: boolean;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setReason('');
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onConfirm={() => onConfirm(reason)}
      title={t('admin:claim.review.download_document_cta')}
      loading={loading}
      confirmLabel={t('admin:claim.review.download_document_cta')}
      cancelLabel={t('admin:claim.review.reject_dialog.cancel')}
    >
      <FormField
        label={t('admin:claim.review.reject_dialog.reason_label')}
        helper={t('admin:claim.review.reject_dialog.reason_helper')}
      >
        <Textarea
          rows={3}
          maxLength={500}
          showCounter
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </FormField>
      {error ? (
        <Banner tone="danger" className="mt-3">
          {error}
        </Banner>
      ) : null}
    </Dialog>
  );
}

// ─── Approve dialog ───────────────────────────────────────────────────────────

interface ApproveFormValues {
  readonly adminOverrideNoDocs?: boolean;
  readonly reason?: string;
}

function ApproveDialog({
  open,
  hasCommercialRegister,
  canOverrideNoDocs,
  onClose,
  onSuccess,
}: {
  open: boolean;
  hasCommercialRegister: boolean;
  canOverrideNoDocs: boolean;
  onClose: () => void;
  onSuccess: (values: ApproveFormValues) => void;
}) {
  const { t } = useI18n();
  const [serverError, setServerError] = useState<string | null>(null);
  const form = useForm<ApproveFormValues>({
    resolver: zodResolver(ApproveVenueClaimInputSchema),
    defaultValues: { adminOverrideNoDocs: false, reason: '' },
  });

  useEffect(() => {
    if (open) {
      form.reset({ adminOverrideNoDocs: false, reason: '' });

      setServerError(null);
    }
  }, [open, form]);

  const override = form.watch('adminOverrideNoDocs');

  const submit = form.handleSubmit((values) => {
    setServerError(null);
    onSuccess({
      ...(values.adminOverrideNoDocs ? { adminOverrideNoDocs: true } : {}),
      ...(values.reason ? { reason: values.reason } : {}),
    });
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:claim.review.approve_dialog.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('admin:claim.review.approve_dialog.cancel')}
          </Button>
          <Button
            variant="primary"
            type="button"
            loading={form.formState.isSubmitting}
            onClick={() => void submit()}
          >
            {t('admin:claim.review.approve_dialog.confirm')}
          </Button>
        </>
      }
    >
      <p className="text-[13px] text-ink-2 leading-snug mb-3">
        {t('admin:claim.review.approve_dialog.body')}
      </p>

      {!hasCommercialRegister ? (
        <Banner tone="warning" className="mb-3">
          {t('admin:claim.review.approve_dialog.override_no_docs_helper')}
        </Banner>
      ) : null}

      {serverError ? (
        <Banner tone="danger" className="mb-3">
          {serverError}
        </Banner>
      ) : null}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="space-y-3"
      >
        {canOverrideNoDocs ? (
          <FormField label={t('admin:claim.review.approve_dialog.override_no_docs_label')}>
            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox
                checked={override === true}
                onChange={(e) =>
                  form.setValue('adminOverrideNoDocs', e.target.checked, { shouldDirty: true })
                }
              />
              <span className="text-[12.5px] text-ink-2 leading-snug">
                {t('admin:claim.review.approve_dialog.override_no_docs_helper')}
              </span>
            </label>
          </FormField>
        ) : null}

        <FormField
          label={t('admin:claim.review.approve_dialog.reason_label')}
          error={form.formState.errors.reason?.message}
        >
          <Textarea rows={3} maxLength={500} showCounter {...form.register('reason')} />
        </FormField>
      </form>
    </Modal>
  );
}

// ─── Reject dialog ────────────────────────────────────────────────────────────

interface PresetReason {
  readonly id: string;
  readonly label: string;
  readonly description: string;
}
/**
 * Preset ids — labels/descriptions resolve through i18n
 * (`admin:claim.review.reject_presets.*`) per identity-f09-f11-007.
 */
const REJECT_PRESET_IDS = [
  'insufficient_docs',
  'cannot_verify',
  'doc_unreadable',
  'wrong_business',
] as const;

function RejectDialog({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: (reason: string) => void;
}) {
  const { t } = useI18n();
  const [serverError, setServerError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [freeformText, setFreeformText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const presets = useMemo<ReadonlyArray<PresetReason>>(
    () =>
      REJECT_PRESET_IDS.map((id) => ({
        id,
        label: t(`admin:claim.review.reject_presets.${id}.label`),
        description: t(`admin:claim.review.reject_presets.${id}.description`),
      })),
    [t],
  );

  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reset dialog state when re-opened; setState in effect is required to react to open prop
      setSelectedId(null);
      setFreeformText('');
      setServerError(null);
    }
  }, [open]);

  const composedReason = (() => {
    const preset = presets.find((p) => p.id === selectedId);
    const head = preset ? preset.label : '';
    return [head, freeformText.trim()].filter(Boolean).join(' — ');
  })();

  const submit = () => {
    setServerError(null);
    // safeParse + friendly field-level message (identity-f09-f11-009) — never
    // dump the raw ZodError JSON blob into the danger Banner.
    const parsed = RejectVenueClaimInputSchema.safeParse({ reason: composedReason });
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setServerError(
        issue?.code === 'too_small'
          ? t('admin:claim.review.reject_dialog.reason_too_short')
          : (issue?.message ?? t('admin:errors.generic')),
      );
      return;
    }
    setSubmitting(true);
    try {
      onSuccess(parsed.data.reason);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:claim.review.reject_dialog.title')}
      footer={
        <Button variant="ghost" onClick={onClose}>
          {t('admin:claim.review.reject_dialog.cancel')}
        </Button>
      }
    >
      <p className="text-[13px] text-ink-2 leading-snug mb-3">
        {t('admin:claim.review.reject_dialog.body')}
      </p>
      {serverError ? (
        <Banner tone="danger" className="mb-3">
          {serverError}
        </Banner>
      ) : null}
      <RejectionReasonPicker
        presets={presets}
        selectedId={selectedId}
        freeformText={freeformText}
        onChange={(id, txt) => {
          setSelectedId(id);
          setFreeformText(txt);
        }}
        onSubmit={() => submit()}
        submitLabel={t('admin:claim.review.reject_dialog.confirm')}
        freeformLabel={t('admin:claim.review.reject_dialog.reason_label')}
        freeformPlaceholder={t('admin:claim.review.reject_dialog.reason_helper')}
        submitting={submitting}
      />
    </Modal>
  );
}

// ─── Revoke dialog ────────────────────────────────────────────────────────────

interface RevokeFormValues {
  readonly reason: string;
}

function RevokeDialog({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: (reason: string) => void;
}) {
  const { t } = useI18n();
  const [serverError, setServerError] = useState<string | null>(null);
  const form = useForm<RevokeFormValues>({
    resolver: zodResolver(RejectVenueClaimInputSchema),
    defaultValues: { reason: '' },
  });

  useEffect(() => {
    if (open) {
      form.reset({ reason: '' });
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setServerError(null);
    }
  }, [open, form]);

  const submit = form.handleSubmit((values) => {
    setServerError(null);
    onSuccess(values.reason);
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:claim.review.revoke_dialog.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('admin:claim.review.revoke_dialog.cancel')}
          </Button>
          <Button
            variant="destructive"
            type="button"
            loading={form.formState.isSubmitting}
            onClick={() => void submit()}
          >
            {t('admin:claim.review.revoke_dialog.confirm')}
          </Button>
        </>
      }
    >
      <p className="text-[13px] text-ink-2 leading-snug mb-3">
        {t('admin:claim.review.revoke_dialog.body')}
      </p>
      {serverError ? (
        <Banner tone="danger" className="mb-3">
          {serverError}
        </Banner>
      ) : null}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="space-y-3"
      >
        <FormField
          label={t('admin:claim.review.revoke_dialog.reason_label')}
          helper={t('admin:claim.review.revoke_dialog.reason_helper')}
          error={form.formState.errors.reason?.message}
          required
        >
          <Textarea rows={3} maxLength={500} showCounter {...form.register('reason')} />
        </FormField>
      </form>
    </Modal>
  );
}

// ─── Site visit notes dialog ──────────────────────────────────────────────────

interface NotesFormValues {
  readonly siteVisitNotes: string;
}

function NotesEditDialog({
  open,
  initialNotes,
  onClose,
  onSuccess,
}: {
  open: boolean;
  initialNotes: string;
  onClose: () => void;
  onSuccess: (notes: string) => void;
}) {
  const { t } = useI18n();
  const [serverError, setServerError] = useState<string | null>(null);
  const form = useForm<NotesFormValues>({
    resolver: zodResolver(VenueClaimSiteVisitNotesInputSchema.transform((d) => d)),
    defaultValues: { siteVisitNotes: initialNotes },
  });

  useEffect(() => {
    if (open) {
      form.reset({ siteVisitNotes: initialNotes });
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setServerError(null);
    }
  }, [open, initialNotes, form]);

  const submit = form.handleSubmit((values) => {
    setServerError(null);
    onSuccess(values.siteVisitNotes ?? '');
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:claim.review.section_site_visit_notes')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('admin:claim.review.reject_dialog.cancel')}
          </Button>
          <Button
            variant="primary"
            type="button"
            loading={form.formState.isSubmitting}
            onClick={() => void submit()}
          >
            {t('admin:claim.review.save_notes_cta')}
          </Button>
        </>
      }
    >
      {serverError ? (
        <Banner tone="danger" className="mb-3">
          {serverError}
        </Banner>
      ) : null}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <FormField label={t('admin:claim.review.section_site_visit_notes')}>
          <Textarea
            rows={6}
            maxLength={2000}
            showCounter
            placeholder={t('admin:claim.review.section_site_visit_notes_placeholder')}
            {...form.register('siteVisitNotes')}
          />
        </FormField>
      </form>
    </Modal>
  );
}

// ─── Documents tab ────────────────────────────────────────────────────────────

function DocumentsTab({
  documents,
  onDownload,
}: {
  documents: readonly VenueClaimDocument[];
  onDownload: (doc: VenueClaimDocument) => void;
}) {
  const { t } = useI18n();

  if (documents.length === 0) {
    return (
      <p className="text-[12.5px] text-ink-3 py-3">{t('admin:claim.review.no_documents_label')}</p>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {documents.map((doc) => (
        <div key={doc.id} className="flex flex-col gap-2">
          <DocumentTile
            filename={doc.fileName}
            sizeBytes={doc.sizeBytes}
            status="done"
            onView={() => onDownload(doc)}
          />
          <div className="flex items-center justify-between text-[11.5px] text-ink-3 ps-1">
            <Badge tone="info" size="sm">
              {doc.documentType}
            </Badge>
            <Button variant="ghost" size="sm" onClick={() => onDownload(doc)}>
              {t('admin:claim.review.download_document_cta')}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function VenueClaimReviewPage() {
  const { t, locale } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { adminUser } = useAuth();

  const isSuperAdmin = adminUser?.role === 'super_admin';
  // F42 D5 read/decide split (identity-f09-f11-001): support + finance hold
  // 'venue_claims:read' (view queue + detail + docs) but NOT
  // 'venue_claims:review' (approve / reject / revoke / site-visit-notes).
  // Hide the decide affordances for read-only actors — the server enforces
  // the same split on every mutation route.
  const canReview = adminUser?.permissions.includes('venue_claims:review') ?? false;

  const [detail, setDetail] = useState<VenueClaimDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);

  const [downloadDoc, setDownloadDoc] = useState<VenueClaimDocument | null>(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await adminVenueClaimApi.detail(id);
      setDetail(data);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchDetail();
  }, [fetchDetail]);

  const handleApprove = async (body: { adminOverrideNoDocs?: boolean; reason?: string }) => {
    if (!detail) return;
    try {
      await adminVenueClaimApi.approve(detail.claim.id, body);
      setApproveOpen(false);
      toast.push({ tone: 'success', title: t('admin:claim.review.approve_cta') });
      void fetchDetail();
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    }
  };

  const handleReject = async (reason: string) => {
    if (!detail) return;
    try {
      await adminVenueClaimApi.reject(detail.claim.id, { reason });
      setRejectOpen(false);
      toast.push({ tone: 'success', title: t('admin:claim.review.reject_cta') });
      void fetchDetail();
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    }
  };

  const handleRevoke = async (reason: string) => {
    if (!detail) return;
    try {
      await adminVenueClaimApi.revoke(detail.claim.id, { reason });
      setRevokeOpen(false);
      toast.push({ tone: 'success', title: t('admin:claim.review.revoke_cta') });
      void fetchDetail();
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    }
  };

  const handleSaveNotes = async (notes: string) => {
    if (!detail) return;
    try {
      await adminVenueClaimApi.updateSiteVisitNotes(detail.claim.id, {
        siteVisitNotes: notes.length > 0 ? notes : null,
      });
      setNotesOpen(false);
      toast.push({ tone: 'success', title: t('admin:claim.review.save_notes_cta') });
      void fetchDetail();
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    }
  };

  const startDownload = (doc: VenueClaimDocument) => {
    setDownloadDoc(doc);
    setDownloadError(null);
  };

  const confirmDownload = async (reason: string) => {
    if (!detail || !downloadDoc) return;
    setDownloadLoading(true);
    setDownloadError(null);
    try {
      const result = await adminVenueClaimApi.getDocumentDownloadUrl(
        detail.claim.id,
        downloadDoc.id,
        reason.trim() ? reason.trim() : undefined,
      );
      // Open in a new tab — the URL is good for 5 minutes.
      window.open(result.url, '_blank', 'noopener,noreferrer');
      setDownloadDoc(null);
    } catch (err) {
      setDownloadError(translateApiError(err, t));
    } finally {
      setDownloadLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size={32} />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="space-y-4">
        <Banner tone="danger">{error ?? t('admin:errors.generic')}</Banner>
        <Button variant="ghost" onClick={() => navigate('/venue-claims')}>
          {t('admin:claim.review.back_to_queue')}
        </Button>
      </div>
    );
  }

  const { claim, venue, account, claimant, documents } = detail;
  const isPending = claim.status === 'pending';
  const isApproved = claim.status === 'approved';
  const hasCommercialRegister = documents.some((d) => d.documentType === 'commercial_register');

  // Timeline lifecycle items — assemble each item with conditional timestamp
  // so `exactOptionalPropertyTypes: true` is satisfied (undefined would widen
  // the type, an absent key passes type-checking).
  const decidedTimestamp = claim.decidedAt
    ? formatDate(claim.decidedAt, locale)
    : claim.withdrawnAt
      ? formatDate(claim.withdrawnAt, locale)
      : null;
  const timelineItems: VerificationTimelineItem[] = [
    {
      id: 'created',
      label: t('admin:claim.queue.filter_status_pending'),
      timestamp: formatDate(claim.createdAt, locale),
      state: 'done',
    },
    {
      id: 'submitted',
      label: t('admin:claim.queue.column_submitted'),
      state: claim.submittedAt ? 'done' : 'pending',
      ...(claim.submittedAt ? { timestamp: formatDate(claim.submittedAt, locale) } : {}),
    },
    {
      id: 'decided',
      label:
        claim.status === 'approved'
          ? t('admin:claim.queue.filter_status_approved')
          : claim.status === 'rejected'
            ? t('admin:claim.queue.filter_status_rejected')
            : claim.status === 'withdrawn'
              ? t('admin:claim.queue.filter_status_withdrawn')
              : claim.status === 'revoked_by_admin'
                ? t('admin:claim.queue.filter_status_revoked')
                : t('admin:claim.queue.filter_status_pending'),
      state:
        claim.status === 'approved'
          ? 'done'
          : claim.status === 'rejected' || claim.status === 'revoked_by_admin'
            ? 'failed'
            : claim.status === 'pending'
              ? 'current'
              : 'pending',
      ...(decidedTimestamp ? { timestamp: decidedTimestamp } : {}),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <button
          type="button"
          className="text-[12px] text-accent hover:underline underline-offset-2"
          onClick={() => navigate('/venue-claims')}
        >
          &larr; {t('admin:claim.review.back_to_queue')}
        </button>
        <h1 className="text-h1 font-bold text-ink">{t('admin:claim.review.title')}</h1>
        <StatusChip
          status={STATUS_CHIP_MAP[claim.status] ?? 'pending'}
          pulse={claim.status === 'pending'}
          label={t(`admin:claim.queue.filter_status_${statusKey(claim.status)}`)}
        />
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left column — venue context */}
        <div className="space-y-4">
          <Card>
            <h2 className="text-[13px] font-semibold text-ink mb-3">
              {t('admin:claim.review.section_venue')}
            </h2>
            {venue ? (
              <div className="space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-medium text-[15px] text-ink">{venue.name}</span>
                </div>
                <span className="font-mono text-[12px] text-ink-3 block">{venue.slug}</span>
              </div>
            ) : (
              <EmptyState
                icon={<UIIcon name="pin" size={28} color="currentColor" />}
                title={t('admin:claim.queue.column_venue')}
              />
            )}
          </Card>
        </div>

        {/* Right column — claimant + account + tabs */}
        <div className="space-y-4">
          {/* Claimant */}
          <Card>
            <h2 className="text-[13px] font-semibold text-ink mb-3">
              {t('admin:claim.review.section_claimant')}
            </h2>
            {claimant ? (
              <div className="flex items-start gap-3">
                <Avatar name={claimant.displayName} size="md" />
                <div className="flex flex-col gap-0.5 min-w-0">
                  <span className="font-medium text-[14px] text-ink">{claimant.displayName}</span>
                  <span className="font-mono text-[12px] text-ink-3">{claimant.email}</span>
                </div>
              </div>
            ) : (
              <p className="text-[12.5px] text-ink-3">—</p>
            )}
          </Card>

          {/* B2B account */}
          <Card>
            <h2 className="text-[13px] font-semibold text-ink mb-3">
              {t('admin:claim.review.section_account')}
            </h2>
            {account ? (
              <dl className="grid grid-cols-1 gap-y-2 text-[12.5px]">
                <div>
                  <dt className="text-ink-3">
                    {t('admin:claim.b2b_accounts.column_display_name')}
                  </dt>
                  <dd className="text-ink font-medium">{account.displayName}</dd>
                </div>
                <div>
                  <dt className="text-ink-3">{t('admin:claim.b2b_accounts.column_owner')}</dt>
                  <dd className="text-ink">{claimant?.email ?? '—'}</dd>
                </div>
              </dl>
            ) : (
              <p className="text-[12.5px] text-ink-3">—</p>
            )}
          </Card>
        </div>
      </div>

      {/* Tabs: documents / history / notes */}
      <Card>
        <Tabs defaultValue="documents">
          <TabList aria-label={t('admin:claim.review.title')}>
            <Tab value="documents">{t('admin:claim.review.section_documents')}</Tab>
            <Tab value="history">{t('admin:claim.review.section_history')}</Tab>
            <Tab value="notes">{t('admin:claim.review.section_site_visit_notes')}</Tab>
          </TabList>

          <TabPanel value="documents">
            <DocumentsTab documents={documents} onDownload={startDownload} />
          </TabPanel>

          <TabPanel value="history">
            <VerificationTimeline items={timelineItems} />
            {claim.rejectionReason ? (
              <div className="mt-4 bg-surface-2 rounded p-3 text-[12px] text-ink">
                <span className="block text-[11px] text-ink-3 mb-1">
                  {t('admin:claim.review.reject_dialog.reason_label')}
                </span>
                {claim.rejectionReason}
              </div>
            ) : null}
          </TabPanel>

          <TabPanel value="notes">
            <div className="space-y-3">
              <div className="bg-surface-2 rounded p-3 text-[12.5px] text-ink whitespace-pre-wrap min-h-[80px]">
                {claim.siteVisitNotes && claim.siteVisitNotes.length > 0
                  ? claim.siteVisitNotes
                  : t('admin:claim.review.section_site_visit_notes_placeholder')}
              </div>
              {canReview ? (
                <Button variant="secondary" size="sm" onClick={() => setNotesOpen(true)}>
                  {t('admin:claim.review.save_notes_cta')}
                </Button>
              ) : null}
            </div>
          </TabPanel>
        </Tabs>
      </Card>

      {/* Sticky action bar — decide affordances only for venue_claims:review */}
      {canReview ? (
        <div className="sticky bottom-0 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-bg/95 backdrop-blur border-t border-hairline-2">
          <div className="flex flex-wrap items-center gap-3 justify-end">
            {!hasCommercialRegister && isPending ? (
              <Chip>{t('admin:claim.review.no_documents_label')}</Chip>
            ) : null}
            {isPending ? (
              <>
                <Button variant="secondary" onClick={() => setRejectOpen(true)}>
                  {t('admin:claim.review.reject_cta')}
                </Button>
                <Button variant="primary" onClick={() => setApproveOpen(true)}>
                  {t('admin:claim.review.approve_cta')}
                </Button>
              </>
            ) : null}
            {isApproved ? (
              <Button variant="destructive" onClick={() => setRevokeOpen(true)}>
                {t('admin:claim.review.revoke_cta')}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Dialogs */}
      <ApproveDialog
        open={approveOpen}
        hasCommercialRegister={hasCommercialRegister}
        canOverrideNoDocs={isSuperAdmin}
        onClose={() => setApproveOpen(false)}
        onSuccess={(values) => void handleApprove(values)}
      />

      <RejectDialog
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        onSuccess={(reason) => void handleReject(reason)}
      />

      <RevokeDialog
        open={revokeOpen}
        onClose={() => setRevokeOpen(false)}
        onSuccess={(reason) => void handleRevoke(reason)}
      />

      <NotesEditDialog
        open={notesOpen}
        initialNotes={claim.siteVisitNotes ?? ''}
        onClose={() => setNotesOpen(false)}
        onSuccess={(notes) => void handleSaveNotes(notes)}
      />

      <DocumentDownloadDialog
        open={downloadDoc !== null}
        loading={downloadLoading}
        error={downloadError}
        onClose={() => setDownloadDoc(null)}
        onConfirm={(reason) => void confirmDownload(reason)}
      />
    </div>
  );
}
